import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Button from '@material-ui/core/Button';
import SaveIcon from '@material-ui/icons/Save';
import Alert from '@material-ui/lab/Alert';
import { getConfig, saveConfig, restartMcp } from '../../actions/config.actions';

const useStyles = makeStyles((theme) => ({
  group: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  field: { marginBottom: theme.spacing(2), maxWidth: 480 },
  actions: { marginTop: theme.spacing(1), marginBottom: theme.spacing(3) },
  envNote: { color: '#888', marginTop: theme.spacing(2) },
  desc: { color: '#888' }
}));

const AdminConfigPage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const config = useSelector(state => state.config);

  // Local dirty edits keyed by config key. Absent key = unchanged.
  const [edits, setEdits] = useState({});
  const [message, setMessage] = useState(null); // {severity, text}

  useEffect(() => {
    dispatch(getConfig());
  }, [dispatch]);

  const setEdit = (key, value) => {
    setEdits(prev => ({ ...prev, [key]: value }));
    setMessage(null);
  };

  // Effective value shown in a field: local edit if present, else server value.
  const valueOf = (item) => (edits.hasOwnProperty(item.key) ? edits[item.key] : item.value);

  const onSave = () => {
    if (Object.keys(edits).length === 0) {
      setMessage({ severity: 'info', text: 'No changes to save.' });
      return;
    }
    dispatch(saveConfig(edits)).then(() => {
      setEdits({});
      setMessage({ severity: 'success', text: 'Configuration saved.' });
    }).catch(() => {
      setMessage({ severity: 'error', text: 'Failed to save configuration.' });
    });
  };

  const onRestart = () => {
    dispatch(restartMcp());
    setMessage({ severity: 'info', text: 'Restart requested — the MCP server will rebind to the configured port within a few seconds; status will refresh.' });
  };

  const renderField = (item) => {
    if (item.type === 'boolean') {
      return (
        <FormControl fullWidth className={classes.field} key={item.key}>
          <FormControlLabel
            control={
              <Switch
                checked={!!valueOf(item)}
                onChange={e => setEdit(item.key, e.target.checked)}
              />
            }
            label={item.label}
          />
          <Typography variant="caption" className={classes.desc}>{item.description}</Typography>
        </FormControl>
      );
    }

    if (item.type === 'secret') {
      return (
        <TextField
          key={item.key}
          className={classes.field}
          fullWidth
          type="password"
          label={item.label}
          value={edits.hasOwnProperty(item.key) ? edits[item.key] : ''}
          placeholder={item.isSet ? '••••••••' : ''}
          onChange={e => setEdit(item.key, e.target.value)}
          helperText={(item.isSet ? 'Currently set — leave blank to keep. ' : 'Not set. ') + item.description}
          InputLabelProps={{ shrink: true }}
        />
      );
    }

    // string / number
    return (
      <TextField
        key={item.key}
        className={classes.field}
        fullWidth
        type={item.type === 'number' ? 'number' : 'text'}
        label={item.label}
        value={valueOf(item) === undefined || valueOf(item) === null ? '' : valueOf(item)}
        onChange={e => setEdit(item.key, e.target.value)}
        helperText={item.description}
        InputLabelProps={{ shrink: true }}
      />
    );
  };

  if (config.apiState === 'init' || config.apiState === 'get') {
    return <Typography variant="body2">Loading configuration…</Typography>;
  }

  return (
    <div>
      <Typography variant="h6" gutterBottom>Configuration</Typography>

      {message && <Alert severity={message.severity} style={{ marginBottom: 16 }}>{message.text}</Alert>}

      {config.groups.map(group => (
        <Paper key={group.category} className={classes.group}>
          <Typography variant="subtitle1" gutterBottom><strong>{group.category}</strong></Typography>
          {group.items.map(renderField)}
        </Paper>
      ))}

      <div className={classes.actions}>
        <Button variant="contained" color="primary" endIcon={<SaveIcon />} onClick={onSave}>
          Save
        </Button>
      </div>

      {config.mcp &&
        <Paper className={classes.group}>
          <Typography variant="subtitle1" gutterBottom><strong>MCP server</strong></Typography>
          <Typography variant="body2">
            Currently bound to port <strong>{config.mcp.boundPort || 'unknown'}</strong>
            {config.mcp.configuredPort != null && <> · configured: <strong>{config.mcp.configuredPort}</strong></>}
          </Typography>
          {config.mcp.boundPort && config.mcp.configuredPort != null && config.mcp.boundPort !== config.mcp.configuredPort &&
            <Alert severity="warning" style={{ marginTop: 8 }}>
              Running on port {config.mcp.boundPort} but configured for {config.mcp.configuredPort} — restart to apply.
            </Alert>}
          <Button variant="outlined" color="primary" style={{ marginTop: 8 }} onClick={onRestart}>
            Restart MCP server
          </Button>
          <Typography variant="caption" className={classes.envNote} component="div">
            Applies a changed MCP port in-process (no host access needed). Remember to point the reverse
            proxy for the public MCP URL at the configured port.
          </Typography>
        </Paper>}

      {config.envManaged && config.envManaged.length > 0 &&
        <Typography variant="caption" className={classes.envNote} component="div">
          Managed in <code>.env</code> (bootstrap, not editable here): {config.envManaged.join(', ')}
        </Typography>}
    </div>
  );
};

export default AdminConfigPage;
